import type { ReactNode } from 'react';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { rentalCompanyLogoOf } from './company_logos/rental_company_logos.js';
export { rentalCompanyLogoOf } from './company_logos/rental_company_logos.js';
import type { RentalCompanyView } from './models.js';

/** O banco interno de logos pelo código, para quem desenha a logo fora destes dois lugares
 *  (o card de aluguel do itinerário, #6004). */

/**
 * A marca da locadora numa LINHA de lista (o filtro de Locadora, o select das
 * Preferências), ao lado do nome — por isso decorativa (`alt=""`): o nome já
 * está escrito. A logo do rental-car-api é o retângulo da marca com o fundo dela
 * (`…/rental-car/company/<código>.jpeg`), e a proporção varia de locadora para
 * locadora (~2:1 a ~2,7:1): a altura é fixa e a largura segue a imagem, para o
 * raio cair no canto da logo e não numa faixa vazia ao redor dela — a mesma
 * régua da logo no card da oferta. Sem imagem (a maioria das locadoras, o CDN
 * devolve 404), nada: o nome sozinho já identifica.
 */
export function RentalCompanyMark({ url }: { readonly url: string | undefined }): ReactNode {
  const image = useImageFallback(url);
  if (!image.src) return null;
  return (
    <img
      className="h-5 w-auto max-w-16 shrink-0 rounded-sm object-contain"
      src={image.src}
      alt=""
      loading="lazy"
      onError={image.onError}
    />
  );
}

/**
 * A logo da locadora no CARD da oferta, onde ela é o único lugar que nomeia a
 * locadora — por isso com nome acessível. Três quedas (#5125): a logo do banco
 * interno pelo código; senão a imagem do fornecedor numa caixa de 72×22
 * (`contain`, fundo branco: o JPEG traz fundo próprio e proporção variável);
 * senão, e quando a imagem quebra, o nome num chip de texto.
 */
export function RentalCompanyLogo({ company }: { readonly company: RentalCompanyView }): ReactNode {
  const logo = rentalCompanyLogoOf(company.code);
  const image = useImageFallback(logo ? undefined : company.logoUrl);
  if (logo) {
    return (
      <img
        className="shrink-0 object-contain"
        src={logo.src}
        width={logo.width}
        height={logo.height}
        alt={company.name}
      />
    );
  }
  if (image.src) {
    return (
      <img
        className="bg-card h-[22px] w-[72px] shrink-0 rounded-sm object-contain"
        src={image.src}
        alt={company.name}
        loading="lazy"
        onError={image.onError}
      />
    );
  }
  return (
    <span className="bg-accent text-foreground inline-block h-[22px] max-w-full truncate rounded-sm px-2 text-xs leading-[22px] font-medium">
      {company.name}
    </span>
  );
}
